import {
  getStoredUtmPayload,
  persistUtmParamsFromCurrentUrl,
  type UtmTouch,
} from './utm';

type TrackingResponse = {
  ok: boolean;
  tracking_id?: string;
};

type CreateTrackingEventOptions = {
  eventType?: string;
  eventId?: string;
  phone?: string;
  email?: string;
  metadata?: Record<string, unknown>;
};

type OpenOptions = {
  newTab?: boolean;
};

const INSPIRA_WHATSAPP_PHONE = '5519988332466';
const DEFAULT_WHATSAPP_MESSAGE = 'Olá, gostaria de criar uma música personalizada';
const VISITOR_ID_KEY = 'inspira_visitor_id';
const SESSION_ID_KEY = 'inspira_session_id';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

function canUseBrowser() {
  return typeof window !== 'undefined';
}

function parseWhatsAppUrl(href: string): { phone: string; message: string } | null {
  let url: URL;
  try {
    url = new URL(href, canUseBrowser() ? window.location.origin : 'https://inspiramusicas.com.br');
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, '');
  if (host === 'wa.me') {
    const phone = url.pathname.replace(/\D/g, '');
    if (phone !== INSPIRA_WHATSAPP_PHONE) return null;
    return {
      phone,
      message: url.searchParams.get('text') || DEFAULT_WHATSAPP_MESSAGE,
    };
  }

  if (host === 'api.whatsapp.com' || host === 'web.whatsapp.com') {
    const phone = (url.searchParams.get('phone') || '').replace(/\D/g, '');
    if (phone !== INSPIRA_WHATSAPP_PHONE) return null;
    return {
      phone,
      message: url.searchParams.get('text') || DEFAULT_WHATSAPP_MESSAGE,
    };
  }

  return null;
}

export function isInspiraWhatsAppUrl(href: string): boolean {
  return parseWhatsAppUrl(href) !== null;
}

export function buildWhatsAppUrlWithTracking(href: string, trackingId?: string): string {
  const parsed = parseWhatsAppUrl(href);
  if (!parsed) return href;
  void trackingId;
  return `https://wa.me/${parsed.phone}?text=${encodeURIComponent(parsed.message.trim() || DEFAULT_WHATSAPP_MESSAGE)}`;
}

function randomId(prefix: string): string {
  const random = globalThis.crypto?.randomUUID?.()
    || `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
  return `${prefix}_${random}`;
}

function storedId(storage: Storage, key: string, prefix: string): string {
  try {
    const current = storage.getItem(key);
    if (current) return current;
    const created = randomId(prefix);
    storage.setItem(key, created);
    return created;
  } catch {
    return randomId(prefix);
  }
}

function cookieValue(name: string): string | null {
  const prefix = `${name}=`;
  const raw = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return raw ? decodeURIComponent(raw.slice(prefix.length)) : null;
}

function metaBrowserIds(stored: ReturnType<typeof getStoredUtmPayload>) {
  const fbp = cookieValue('_fbp');
  const cookieFbc = cookieValue('_fbc');
  const touch = stored.last_touch || stored.first_touch;
  const fbclid = touch?.fbclid;
  const capturedAt = touch?.captured_at ? Date.parse(touch.captured_at) : Date.now();
  const fbc = cookieFbc || (fbclid ? `fb.1.${Number.isFinite(capturedAt) ? capturedAt : Date.now()}.${fbclid}` : null);
  return { fbp, fbc };
}

function fireMetaBrowserEvent(eventName: string, eventId: string) {
  if (typeof window.fbq !== 'function') return;
  window.fbq('trackCustom', eventName, {}, { eventID: eventId });
}

export async function createTrackingEvent(
  options: CreateTrackingEventOptions = {},
): Promise<TrackingResponse | null> {
  if (!canUseBrowser()) return null;

  persistUtmParamsFromCurrentUrl();
  const stored = getStoredUtmPayload();
  const eventId = options.eventId || randomId('web');
  const visitorId = storedId(window.localStorage, VISITOR_ID_KEY, 'visitor');
  const sessionId = storedId(window.sessionStorage, SESSION_ID_KEY, 'session');
  const { fbp, fbc } = metaBrowserIds(stored);
  const payload = {
    event_type: options.eventType || 'whatsapp_click',
    event_id: eventId,
    visitor_id: visitorId,
    session_id: sessionId,
    client_event_time: new Date().toISOString(),
    fbp,
    fbc,
    first_touch: stored.first_touch,
    last_touch: stored.last_touch,
    landing_url: stored.last_touch?.landing_url || stored.first_touch?.landing_url || window.location.href,
    referrer: stored.last_touch?.referrer || stored.first_touch?.referrer || document.referrer || '',
    phone: options.phone,
    email: options.email,
    metadata: options.metadata || {},
  };

  const res = await fetch('/api/checkout?action=tracking-whatsapp-click', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok) return null;
  return data;
}

export async function recordLandingPageView(ctaId = 'landing_page'): Promise<void> {
  if (!canUseBrowser()) return;
  const pageKey = `inspira_lpv:${window.location.pathname}:${window.location.search}`;
  try {
    if (window.sessionStorage.getItem(pageKey)) return;
    window.sessionStorage.setItem(pageKey, '1');
  } catch {
    // O evento continua mesmo quando o navegador bloqueia storage.
  }
  const eventId = randomId('lpv');
  fireMetaBrowserEvent('LandingPageView', eventId);
  await createTrackingEvent({
    eventType: 'landing_page_view',
    eventId,
    metadata: { source: 'website_whatsapp', cta_id: ctaId },
  });
}

export async function openTrackedWhatsAppUrl(
  href: string,
  options: OpenOptions = {},
): Promise<void> {
  if (!canUseBrowser()) return;

  const newTab = options.newTab ?? true;
  const fallbackUrl = buildWhatsAppUrlWithTracking(href);
  const parsed = parseWhatsAppUrl(href);
  const pendingWindow = newTab ? window.open('', '_blank') : null;
  if (pendingWindow) pendingWindow.opener = null;

  try {
    const eventId = randomId('wa_click');
    fireMetaBrowserEvent('WhatsAppClick', eventId);
    const tracking = await createTrackingEvent({
      eventType: 'whatsapp_click',
      eventId,
      metadata: {
        source: 'website_whatsapp',
        cta_id: 'whatsapp_primary',
        target_phone: parsed?.phone || INSPIRA_WHATSAPP_PHONE,
        device: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
      },
    });
    const finalUrl = buildWhatsAppUrlWithTracking(href, tracking?.tracking_id);
    if (pendingWindow) {
      pendingWindow.location.href = finalUrl;
      return;
    }
    if (newTab) {
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.href = finalUrl;
  } catch {
    if (pendingWindow) {
      pendingWindow.location.href = fallbackUrl;
      return;
    }
    if (newTab) {
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.href = fallbackUrl;
  }
}

export function currentTrackingTouches(): {
  first_touch: UtmTouch | null;
  last_touch: UtmTouch | null;
} {
  if (!canUseBrowser()) return { first_touch: null, last_touch: null };
  persistUtmParamsFromCurrentUrl();
  return getStoredUtmPayload();
}
